/**
 * API function to register user for a Mathlete 101 batch
 * Based on starry-night reference implementation
 */

import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { MATHLETE_CONFIG } from '@/features/user-notifications/utils/constants';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type {
  RegisterForMathleteBatchRequestBody,
  RegisterForMathleteBatchResponse,
} from '../types';

/**
 * Registers a user for a specific Mathlete batch
 *
 * @param body - Request body containing batch_id and variant_id
 * @returns Promise resolving to registration response
 *
 * @example
 * ```ts
 * const result = await registerForMathleteBatch({
 *   batch_id: 'batch-123',
 *   variant_id: 'variant-456'
 * });
 * ```
 */
export const registerForMathleteBatch = async (
  body: RegisterForMathleteBatchRequestBody
): Promise<RegisterForMathleteBatchResponse> => {
  const response = await axios.post<RegisterForMathleteBatchResponse>(
    MATHLETE_CONFIG.apiEndpoints.registerForBatch,
    body
  );

  return response;
};

/**
 * MSW handler for registering for a Mathlete batch
 */
export const registerForMathleteBatchMockHandler = http.post(
  `${env.API_URL}/user/v1/enroll_existing_profile`,
  async ({ request }) => {
    await networkDelay();

    const body = (await request.json()) as RegisterForMathleteBatchRequestBody;

    // Validate required fields
    if (!body.batch_id) {
      return HttpResponse.json(
        {
          status: 'error',
          message: 'Missing required field: batch_id',
        },
        { status: 400 }
      );
    }

    const response: RegisterForMathleteBatchResponse = {
      status: 'success',
      message: 'Successfully registered for Mathlete 101 batch',
      profile_id: 'test-profile-id',
      enrolled_courses: [
        {
          course_id: 'mathlete-101-course-id',
          course_name: 'Mathlete 101',
          created_at: new Date().toISOString(),
          status: 'active',
          batch_id: body.batch_id,
          payment_id: null,
        },
      ],
    };

    return HttpResponse.json(response, { status: 200 });
  }
);
